import { gql } from '@apollo/client';

/**
 * Query to get user addresses
 */
export const GET_USER_ADDRESSES = gql`
  query GetUserAddresses($userId: uuid!, $shopId: uuid!) {
    mst_user_address(
      where: {
        user: { _eq: $userId }
        shop_id: { _eq: $shopId }
        isdeleted: { _neq: true }
      }
      order_by: { is_default: desc, created_at: desc }
    ) {
      id
      name
      address
      city
      state
      pincode
      landmark
      mobile
      email
      save_address_as
      is_default
      isdeleted
      created_at
      updated_at
      shop_id
      user
    }
  }
`;

/** All **`mst_user_address`** rows for **`user`** (any `shop_id` — needed when addresses were saved against the platform-registry `mst_users`). */
export const GET_USER_ADDRESSES_BY_USER = gql`
  query GetUserAddressesByUserScoped($userId: uuid!) {
    mst_user_address(
      where: { user: { _eq: $userId }, isdeleted: { _neq: true } }
      order_by: { is_default: desc, created_at: desc }
    ) {
      id
      name
      address
      city
      state
      pincode
      landmark
      mobile
      email
      save_address_as
      is_default
      isdeleted
      created_at
      updated_at
      shop_id
      user
    }
  }
`;

/**
 * Mutation to insert a new user address
 */
export const INSERT_USER_ADDRESS = gql`
  mutation InsertUserAddress($address: mst_user_address_insert_input!) {
    insert_mst_user_address_one(object: $address) {
      id
      name
      address
      city
      state
      pincode
      landmark
      mobile
      email
      save_address_as
      is_default
      isdeleted
      created_at
      updated_at
      shop_id
      user
    }
  }
`;

/**
 * Mutation to update a user address
 */
export const UPDATE_USER_ADDRESS = gql`
  mutation UpdateUserAddress(
    $id: uuid!
    $address: mst_user_address_set_input!
  ) {
    update_mst_user_address_by_pk(pk_columns: { id: $id }, _set: $address) {
      id
      name
      address
      city
      state
      pincode
      landmark
      mobile
      email
      save_address_as
      is_default
      isdeleted
      updated_at
    }
  }
`;

/**
 * Mutation to delete a user address (soft delete)
 */
export const DELETE_USER_ADDRESS = gql`
  mutation DeleteUserAddress($id: uuid!) {
    update_mst_user_address_by_pk(
      pk_columns: { id: $id }
      _set: { isdeleted: true }
    ) {
      id
      isdeleted
    }
  }
`;

/**
 * Query to get user bookings/orders
 */
export const GET_USER_BOOKINGS = gql`
  query GetUserBookings($userId: uuid!, $shopId: uuid!) {
    mst_bookings(
      where: { user_id: { _eq: $userId }, shop_id: { _eq: $shopId } }
      order_by: { created_at: desc }
    ) {
      accepted_at
      address
      bill_products
      cancel_reason
      cancelled_at
      commissionAmount
      coupon_code
      coupon_used
      created_at
      date
      customer
      delivery
      dispatched_at
      delivered_at
      id
      inTransit_at
      is_online
      netAmount
      pay_type
      payment_id
      payment_type
      payment_verify
      refund_reason
      refund_status
      rejected_at
      shop_id
      status
      tax
      time
      tracking_image
      tracking_id
      total
      updated_at
      user_id
      userAddress {
        id
        name
        address
        city
        state
        pincode
        landmark
        mobile
      }
      mst_booked_products {
        id
        booking_id
        quantity
        sub_total
        attributes
        status
        return_status
        shop_id
        delivered_at
        mst_products {
          id
          name
          images
        }
      }
    }
  }
`;


/**
 * Mutation to insert a new booking/order with nested booked products
 */
export const INSERT_BOOKING = gql`
  mutation InsertBooking($booking: mst_bookings_insert_input!) {
    insert_mst_bookings_one(object: $booking) {
      accepted_at
      address
      bill_products
      cancel_reason
      cancelled_at
      commissionAmount
      coupon_code
      coupon_used
      created_at
      date
      customer
      delivery
      dispatched_at
      delivered_at
      id
      inTransit_at
      is_online
      netAmount
      pay_type
      payment_id
      payment_type
      payment_verify
      refund_reason
      refund_status
      rejected_at
      shop_id
      status
      tax
      time
      tracking_image
      tracking_id
      total
      updated_at
      user_id
      mst_booked_products {
        id
        booking_id
        product
        quantity
        price
        paid_price
        coupon_price
        saved_price
        sub_total
        attributes
        status
        return_status
        is_review
        review_rating
        star_rating
        review_photos_videos
        delivered_at
        created_at
        updated_at
        shop_id
      }
    }
  }
`;

/**
 * Mutation to insert a booking and decrement product stocks.
 * The stock update runs only when payment is confirmed.
 */
export const INSERT_BOOKING_WITH_STOCK_UPDATE = gql`
  mutation InsertBookingWithStockUpdate(
    $booking: mst_bookings_insert_input!
    $stockUpdates: [mst_product_updates!]!
  ) {
    insert_mst_bookings_one(object: $booking) {
      accepted_at
      address
      bill_products
      cancel_reason
      cancelled_at
      commissionAmount
      coupon_code
      coupon_used
      created_at
      date
      customer
      delivery
      dispatched_at
      delivered_at
      id
      inTransit_at
      is_online
      netAmount
      pay_type
      payment_id
      payment_type
      payment_verify
      refund_reason
      refund_status
      rejected_at
      shop_id
      status
      tax
      time
      tracking_image
      tracking_id
      total
      updated_at
      user_id
      mst_booked_products {
        id
        booking_id
        product
        quantity
        price
        paid_price
        coupon_price
        saved_price
        sub_total
        attributes
        status
        return_status
        is_review
        review_rating
        star_rating
        review_photos_videos
        delivered_at
        created_at
        updated_at
        shop_id
      }
    }
    update_mst_product_many(updates: $stockUpdates) {
      affected_rows
      returning {
        id
        stocks
        quantity
        attributes
      }
    }
  }
`;

/**
 * Query product stock for checkout validation.
 */
export const GET_PRODUCTS_STOCK = gql`
  query GetProductsStock($productIds: [uuid!]!, $shopId: uuid!) {
    mst_product(
      where: {
        id: { _in: $productIds }
        shop_id: { _eq: $shopId }
        isdeleted: { _neq: true }
      }
    ) {
      id
      name
      stocks
      quantity
      attributes
    }
  }
`;

/**
 * Mutation to update booking payment verification
 */
export const UPDATE_BOOKING_PAYMENT = gql`
  mutation UpdateBookingPayment(
    $id: uuid!
    $paymentId: String!
    $paymentVerify: Boolean!
  ) {
    update_mst_bookings_by_pk(
      pk_columns: { id: $id }
      _set: { payment_id: $paymentId, payment_verify: $paymentVerify }
    ) {
      id
      payment_id
      payment_verify
      status
    }
  }
`;
